'use client';

import type { ReactNode } from 'react';
import { HStack } from '@terra/ui-styled-system';
import type { HITLActionRequest } from '@terra/shared-dto-chat-types';
import type { PendingApprovalControls, ToolConfig } from '../tool-config-types';
import { HITLPendingHeaderActions } from './components/HITLPendingHeaderActions';
import { withElevatedBadge } from './components/HitlElevatedBadge';
import { HitlReviewReasonHeaderAction } from './components/HitlReviewReasonHeaderAction';

export type PendingActionControls = {
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  onReview?: () => void;
  onAlwaysAllow?: () => void;
  alwaysAllowPattern?: string;
  disabled?: boolean;
  enableShortcuts?: boolean;
};

export type PendingApprovalSlots = {
  headerActions: ReactNode;
  pendingApproval?: PendingApprovalControls;
};

function withReviewReason({
  reviewReason,
  actions,
}: {
  reviewReason: string | undefined;
  actions: ReactNode;
}): ReactNode {
  if (!reviewReason) {
    return actions;
  }

  return (
    <HStack gap={1} alignItems="center">
      <HitlReviewReasonHeaderAction reason={reviewReason} />
      {actions}
    </HStack>
  );
}

export function buildPendingApprovalSlots({
  toolConfig,
  request,
  controls,
}: {
  toolConfig: Pick<ToolConfig, 'rendersApprovalFooter'>;
  request: Pick<HITLActionRequest, 'elevated' | 'reviewReason'> | undefined;
  controls: PendingActionControls | undefined;
}): PendingApprovalSlots {
  const onApprove = controls?.onApprove;
  const onReject = controls?.onReject;
  const elevated = request?.elevated;
  const reviewReason = request?.reviewReason;

  if (toolConfig.rendersApprovalFooter && controls && onApprove && onReject) {
    return {
      headerActions: withElevatedBadge({
        elevated,
        actions: withReviewReason({ reviewReason, actions: undefined }),
      }),
      pendingApproval: {
        onApprove,
        onReject,
        onEdit: controls.onEdit,
        onAlwaysAllow: controls.onAlwaysAllow,
        alwaysAllowPattern: controls.alwaysAllowPattern,
        disabled: controls.disabled,
        enableShortcuts: controls.enableShortcuts,
      },
    };
  }

  return {
    headerActions: withElevatedBadge({
      elevated,
      actions: withReviewReason({
        reviewReason,
        actions: controls ? (
          <HITLPendingHeaderActions
            onApprove={controls.onApprove}
            onReject={controls.onReject}
            onEdit={controls.onEdit}
            onReview={controls.onReview}
            disabled={controls.disabled}
          />
        ) : undefined,
      }),
    }),
  };
}
